import { Text } from '@fluentui/react-components';
import { Attach12Regular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useRef, useState } from 'react';
import { useStores } from '../../../common/hooks';
import { emailTemplateBannerImageFileConfiguration } from '../../../configurations';
import { fileUploadValidator, getFileNameFromUrl, isValidImageFile } from '../../../shared/functions';
import DragAndDropUploader from '../../shared-components/DragAndDropUploader';
import { FlexBox } from '../../shared-components/flex-box';
import './BannerImageSection.scss';

const BannerImageSectionImpl: React.FC = () => {

  const {
    createOrEditEmailTemplatePageUiStore,
  } = useStores();

  const uploadBannerImageFileRef = useRef<HTMLInputElement>(null);
  const [inputKey, setInputKey] = useState('');
  const [uploadBannerImageFileErrorMessages, setUploadBannerImageFileErrorMessages] = useState<string[]>([]);

  const handleUploadedFile = async (files: FileList): Promise<void> => {
    inputKey === files[0].name ? setInputKey('') : setInputKey(files[0].name);
    if (!files || !files[0] || files.length > 1) return;

    const file = files[0];
    const validationResult = fileUploadValidator(file, emailTemplateBannerImageFileConfiguration);
    if (!validationResult.isValid) {
      setUploadBannerImageFileErrorMessages(validationResult.errorMessages);
      return;
    }

    const isValidImage = await isValidImageFile(file);
    if (!isValidImage) {
      setUploadBannerImageFileErrorMessages(['Select a valid image file.']);
      return;
    }

    setUploadBannerImageFileErrorMessages([]);
    createOrEditEmailTemplatePageUiStore.setBannerImageFile(file);
  };

  const clearUploadedFile = (): void => {
    setUploadBannerImageFileErrorMessages([]);
    createOrEditEmailTemplatePageUiStore.setBannerImageFile(undefined);
  };

  const clearExistingBannerImage = () => {
    createOrEditEmailTemplatePageUiStore.setEmailTemplateRequestBody<undefined>({
      key: 'bannerImageUrl',
      value: undefined,
    });
  };

  const renderPreviewImage = (imgSrc: string): React.JSX.Element => (
    <img
      src={imgSrc}
      width={397}
      height={130}
      alt='preview banner email'
    />
  )

  const renderImage = (bannerImageUrl: string): React.JSX.Element => {
    const fileName = getFileNameFromUrl(bannerImageUrl);
    return (
      <div className='banner-image-container'>
        <img
          src={`/api/email-resources/banner-images/${fileName}`}
          width={397}
          height={130}
          alt='banner email'
        />
      </div>
    );
  };

  return (
    <div className='banner-image-section'>
      <FlexBox
        aligns='center'
        content='space-between'
        className='banner-image-section__header'
      >
        <Text
          weight='semibold'
        >
          Banner image (optional)
        </Text>
        {
          (createOrEditEmailTemplatePageUiStore.emailTemplateRequestBody?.bannerImageUrl || createOrEditEmailTemplatePageUiStore.selectedBannerImageFile) && (
            <button
              onClick={() => createOrEditEmailTemplatePageUiStore.selectedBannerImageFile ? clearUploadedFile() : clearExistingBannerImage()}
            >
              Clear
            </button>
          )
        }
      </FlexBox>
      <DragAndDropUploader
        elementId='bannerImageUploadArea'
        handleDroppedFile={handleUploadedFile}
        isUploadedFile={!!createOrEditEmailTemplatePageUiStore.selectedBannerImageFile}
        errorMessages={uploadBannerImageFileErrorMessages}
      >
        {createOrEditEmailTemplatePageUiStore.emailTemplateRequestBody?.bannerImageUrl || createOrEditEmailTemplatePageUiStore.selectedBannerImageFile ? (
          <>
            {
              createOrEditEmailTemplatePageUiStore.selectedBannerImageFile && (
                <>
                  {renderPreviewImage(URL.createObjectURL(createOrEditEmailTemplatePageUiStore.selectedBannerImageFile))}
                </>
              )
            }
            {
              createOrEditEmailTemplatePageUiStore.emailTemplateRequestBody?.bannerImageUrl && (
                <>
                  {renderImage(createOrEditEmailTemplatePageUiStore.emailTemplateRequestBody.bannerImageUrl)}
                </>
              )
            }
          </>
        ) : (
          <FlexBox
            aligns='center'
            content='center'
            className='uploader-form'
          >
            <Attach12Regular />
            <button onClick={() => uploadBannerImageFileRef.current?.click()}>
              Add file
            </button>
            <span> or drop file here</span>
          </FlexBox>
        )}
      </DragAndDropUploader>
      <input
        key={inputKey}
        type='file'
        ref={uploadBannerImageFileRef}
        onChange={(event) => handleUploadedFile(event.currentTarget.files as FileList)}
        accept={emailTemplateBannerImageFileConfiguration.supportedFileExtensions.join(',')}
        hidden
      />
    </div>
  );
};

export const BannerImageSection = observer(BannerImageSectionImpl);
